import { fetchWithAuth } from '../AuthService';
import { AI_SERVICE_BASE_URL } from '../AiApiService';

/**
 * AI Study Generation API service
 * 
 * Provides access to AI-powered quiz and flashcard generation endpoints.
 */
export const AiStudyGenerationApi = {
  /**
   * Generate quiz from documents using RAG and GPT
   * @param {Object} data - Quiz generation parameters
   * @param {number[]} data.document_ids - Array of document IDs to generate quiz from
   * @param {number} [data.num_questions=20] - Number of questions to generate
   * @param {string} [data.difficulty_level="intermediate"] - Difficulty: "beginner", "intermediate", or "advanced"
   * @param {Object} [data.question_type_distribution] - Question type distribution (e.g., {"multiple_choice": 10, "true_false": 5, "short_answer": 5})
   * @param {number} [data.endeavor_id] - Endeavor ID (optional)
   * @param {number} data.user_id - User ID
   * @param {string} [data.title] - Quiz title (optional)
   * @param {string} [data.description] - Quiz description (optional)
   * @param {Object} [options] - Additional options
   * @param {AbortSignal} [options.signal] - Abort signal for request cancellation
   * @returns {Promise<Object>} Generated quiz with quiz_set_id, questions, cost_usd, and num_questions
   */
  generateQuiz: async (data, options = {}) => {
    const res = await fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/quiz/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      signal: options.signal,
    });

    const raw = await res.text();

    let parsedData = null;
    try {
      parsedData = raw ? JSON.parse(raw) : null;
    } catch (e) {
      // If backend returns non-JSON, keep parsedData null so raw shows in error/logs
      console.error('[AiStudyGenerationApi.generateQuiz] JSON parse error:', e);
    }

    // Helpful debug logs
    console.log('[AiStudyGenerationApi.generateQuiz] status:', res.status, res.ok);
    console.log('[AiStudyGenerationApi.generateQuiz] raw:', raw);
    console.log('[AiStudyGenerationApi.generateQuiz] json:', parsedData);

    if (!res.ok) {
      const msg =
        parsedData?.message ||
        parsedData?.error ||
        raw ||
        `Quiz generation failed (${res.status})`;

      const err = new Error(msg);
      err.status = res.status;
      err.data = parsedData;
      throw err;
    }

    return parsedData;
  },

  /**
   * Generate flashcards from documents using RAG and GPT
   * @param {Object} data - Flashcard generation parameters
   * @param {number[]} data.document_ids - Array of document IDs to generate flashcards from
   * @param {number} [data.num_cards=20] - Number of flashcards to generate
   * @param {number} [data.endeavor_id] - Endeavor ID (optional)
   * @param {number} data.user_id - User ID
   * @param {string} [data.title] - Deck title (optional)
   * @param {string} [data.description] - Deck description (optional)
   * @returns {Promise<Object>} Generated flashcards with deck_id, flashcards, cost_usd, and num_cards
   */
  generateFlashcards: async (data) => {
    const res = await fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/flashcards/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const raw = await res.text();

    let parsedData = null;
    try {
      parsedData = raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error('[AiStudyGenerationApi.generateFlashcards] JSON parse error:', e);
    }

    console.log('[AiStudyGenerationApi.generateFlashcards] status:', res.status, res.ok);
    console.log('[AiStudyGenerationApi.generateFlashcards] raw:', raw);
    console.log('[AiStudyGenerationApi.generateFlashcards] json:', parsedData);

    if (!res.ok) {
      const msg =
        parsedData?.message ||
        parsedData?.error ||
        raw ||
        `Flashcard generation failed (${res.status})`;

      const err = new Error(msg);
      err.status = res.status;
      err.data = parsedData;
      throw err;
    }

    return parsedData;
  },

  /**
   * Grade quiz attempt with short-answer questions using GPT
   * @param {number} attemptId - Quiz attempt ID
   * @param {Object} data - Grading data
   * @param {Object[]} data.questions - Array of questions to grade
   * @param {number} data.questions[].question_id - Question ID
   * @param {string} data.questions[].question_text - Question text
   * @param {string} data.questions[].correct_answer - Correct answer
   * @param {string} data.questions[].user_answer - User's answer
   * @param {string} [data.questions[].explanation] - Answer explanation (optional)
   * @param {number} data.user_id - User ID
   * @returns {Promise<Object>} Grading results with scores, confidence, reasoning, and cost_usd
   */
  gradeQuizAttempt: async (attemptId, data) => {
    const res = await fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/quiz/attempts/${attemptId}/grade`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const raw = await res.text();

    let parsedData = null;
    try {
      parsedData = raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error('[AiStudyGenerationApi.gradeQuizAttempt] JSON parse error:', e);
    }

    console.log('[AiStudyGenerationApi.gradeQuizAttempt] status:', res.status, res.ok);
    console.log('[AiStudyGenerationApi.gradeQuizAttempt] raw:', raw);
    console.log('[AiStudyGenerationApi.gradeQuizAttempt] json:', parsedData);

    if (!res.ok) {
      const msg =
        parsedData?.message ||
        parsedData?.error ||
        raw ||
        `Quiz grading failed (${res.status})`;

      const err = new Error(msg);
      err.status = res.status;
      err.data = parsedData;
      throw err;
    }

    return parsedData;
  },

  /**
   * Get user's flashcard decks
   * @param {number} userId - User ID
   * @param {number} [limit=20] - Maximum number of decks to return
   * @returns {Promise<Object[]>} Array of flashcard decks
   */
  getUserFlashcardDecks: async (userId, limit = 20) => {
    const res = await fetchWithAuth(
      `${AI_SERVICE_BASE_URL}/api/flashcards/user/${userId}?limit=${limit}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const raw = await res.text();

    let parsedData = null;
    try {
      parsedData = raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error('[AiStudyGenerationApi.getUserFlashcardDecks] JSON parse error:', e);
    }

    if (!res.ok) {
      const msg =
        parsedData?.message ||
        parsedData?.error ||
        raw ||
        `Failed to fetch flashcard decks (${res.status})`;

      const err = new Error(msg);
      err.status = res.status;
      err.data = parsedData;
      throw err;
    }

    return parsedData || [];
  },

  /**
   * Get a specific flashcard deck by ID
   * @param {number} deckId - Deck ID
   * @returns {Promise<Object>} Flashcard deck with all cards
   */
  getFlashcardDeck: async (deckId) => {
    const res = await fetchWithAuth(
      `${AI_SERVICE_BASE_URL}/api/flashcards/deck/${deckId}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const raw = await res.text();

    let parsedData = null;
    try {
      parsedData = raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error('[AiStudyGenerationApi.getFlashcardDeck] JSON parse error:', e);
    }

    if (!res.ok) {
      const msg =
        parsedData?.message ||
        parsedData?.error ||
        raw ||
        `Failed to fetch flashcard deck (${res.status})`;

      const err = new Error(msg);
      err.status = res.status;
      err.data = parsedData;
      throw err;
    }

    return parsedData;
  },
};
